import React, { FC, PropsWithChildren, useEffect } from "react";
import { useTranslation } from "i18n";
import { useSessionStorage } from "usehooks-ts";
import {
  SL_MEMBER_GROUP,
  SIGN_IN_CALLBACK_URL,
  useCheckAndSetPortalCookie,
  useAxiosTokenRefresh,
  useAuth,
  useAutoSignin,
  POST_LOGIN_REDIRECT_KEY,
} from "shared-auth";
import { Spinner } from "../../../../Atoms/Spinner/Spinner";
import CommonErrorPopup from "../../../CommonErrorPopup";
import { config } from "../../../../../config";

const Authenticator: FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation();
  const [, setPostLoginRedirect] = useSessionStorage(
    POST_LOGIN_REDIRECT_KEY,
    ""
  );
  const { isLoading, isAuthenticated, error } = useAutoSignin({
    signinMethod: "signinRedirect",
  });
  const auth = useAuth();

  useAxiosTokenRefresh();

  useCheckAndSetPortalCookie();

  const signout = async () => {
    await auth.signoutSilent();
  };

  useEffect(() => {
    if (auth.activeNavigator === "signinRedirect") {
      setPostLoginRedirect(window.location.pathname);
    }

    if (auth.activeNavigator === "signoutSilent") {
      window.location.assign(config.portalApiLogoutUrl);
    }
  }, [auth.activeNavigator, setPostLoginRedirect]);

  if (isLoading || window.location.pathname.includes(SIGN_IN_CALLBACK_URL)) {
    return <Spinner />;
  }

  const userHasEmptyGroup =
    isAuthenticated && !auth.user?.profile[SL_MEMBER_GROUP];

  if (!isAuthenticated || error || userHasEmptyGroup) {
    return (
      <CommonErrorPopup
        errorStatusCode={401}
        popupProps={{
          close: signout,
        }}
        alertMessageProps={{
          buttons: [
            {
              name: t("common:button.label.close"),
              type: "primary",
              click: signout,
            },
          ],
        }}
      />
    );
  }

  return <>{children}</>;
};

export default Authenticator;
